import {
    Route,
    Navigate,
    useParams,
    useNavigate,
    ParamParseKey,
    Routes as Switch,
} from 'react-router-dom';
import { observer } from 'mobx-react';
import { useEffect } from 'react';

import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Breadcrumbs, BreadcrumbsLink } from '@servicetitan/anvil2';

import { withRoot } from '../../utils/with-root';
import { TenantPanelContent } from './tenant-panel-content';
import { Partitions } from '../partitions/partitions';
import { TenantConfiguration } from './tenant-configuration';
import { AccountManagementRoutes, ManageTenantRoutes } from '../../utils/routes';
import { LoadStatus } from '../../../common/enums/load-status';
import { ManageTenantStore } from '../../stores/manage-tenant.store';
import { TenantSmsConfigurationStore } from '../../stores/tenant-sms-configuration.store';
import { ManageTenantLayout } from '../manage-tenant-layout/manage-tenant-layout';
import { Telecom } from '../../telecom/telecom';
import { asParentPath } from '../../../common/utils/as-parent-path';

export const ManageTenant = provide({
    singletons: [ManageTenantStore, TenantSmsConfigurationStore],
})(
    observer(() => {
        const { externalTenantId, externalPartnerId } =
            useParams<ParamParseKey<typeof AccountManagementRoutes.manageTenant>>();

        const [{ pageLoadStatus, init, tenant, showTelecom }] = useDependencies(ManageTenantStore);

        useEffect(() => {
            init(externalPartnerId!, externalTenantId!);
        }, [init, externalPartnerId, externalTenantId]);

        const navigate = useNavigate();

        return (
            <ManageTenantLayout
                panelContent={<TenantPanelContent />}
                error={pageLoadStatus === LoadStatus.Error}
                loading={pageLoadStatus === LoadStatus.Loading}
                breadCrumb={
                    <Breadcrumbs>
                        <BreadcrumbsLink
                            onClick={() => navigate(withRoot(AccountManagementRoutes.tenants))}
                        >
                            Accounts Management
                        </BreadcrumbsLink>
                        <BreadcrumbsLink>Manage Tenant</BreadcrumbsLink>
                    </Breadcrumbs>
                }
                headline={tenant?.displayName}
            >
                {!!tenant && (
                    <Switch>
                        <Route
                            path={asParentPath(ManageTenantRoutes.configuration)}
                            element={<TenantConfiguration />}
                        />
                        <Route path={ManageTenantRoutes.partitions} element={<Partitions />} />
                        {showTelecom && (
                            <Route
                                path={asParentPath(ManageTenantRoutes.telecom)}
                                element={<Telecom />}
                            />
                        )}
                        <Route
                            path="*"
                            element={<Navigate to={ManageTenantRoutes.configuration} />}
                        />
                    </Switch>
                )}
            </ManageTenantLayout>
        );
    }),
);
